import { ApplicationRef, ComponentRef, createComponent, EmbeddedViewRef, EnvironmentInjector, inject, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MaintenanceDiagnostic, MaintenanceNotificationComponent } from './maintenance-notification.component';

const SAFE_OPERATION_NAMES: Record<string, string> = {
	'/api/v1/contracts/show': 'Fragebogen laden',
	'/api/v1/contracts/store': 'Fragebogen speichern',
	'/api/v1/contracts/documents/list': 'Dokumente laden',
	'/api/v1/contracts/documents/upload': 'Dokument hochladen',
	'/api/v1/contracts/documents/delete': 'Dokument löschen',
	'/api/v1/contracts/documents/preview': 'Dokument anzeigen',
	'/api/v1/admin/contracts/documents/list': 'Vertragsdokumente laden',
	'/api/v1/person-documents/work-lock/status': 'Dokumentsperre prüfen',
	'/api/v1/requests/new': 'Messeanfrage laden',
	'/api/v1/requests/store': 'Messeanfrage speichern',
	'/api/v1/requests/calculate': 'Messeanfrage berechnen'
};

@Injectable({
	providedIn: 'root'
})
export class MaintenanceNotificationService {
	private readonly appRef = inject(ApplicationRef);
	private readonly injector = inject(Injector); // Root-Injektor
	private isDisplayed = false;
	private componentRef: ComponentRef<MaintenanceNotificationComponent> | null = null;

	showMaintenanceNotification(error: HttpErrorResponse, requestMethod: string, requestUrl: string): void {
		if (this.isDisplayed) {
			return;
		}
		this.isDisplayed = true;

		// Wir nutzen hier den globalen Injector als EnvironmentInjector.
		// Voraussetzung: Der global injizierte Injector ist auch ein EnvironmentInjector.
		this.componentRef = createComponent(MaintenanceNotificationComponent, {
			environmentInjector: this.injector as EnvironmentInjector
		});
		this.componentRef.instance.diagnostic = this.createDiagnostic(error, requestMethod, requestUrl);
		this.componentRef.instance.dismiss = () => this.hideMaintenanceNotification();
		this.appRef.attachView(this.componentRef.hostView);

		const domElem = (this.componentRef.hostView as EmbeddedViewRef<unknown>).rootNodes[0] as HTMLElement;
		document.body.appendChild(domElem);
		this.componentRef.changeDetectorRef.detectChanges();
	}

	private hideMaintenanceNotification(): void {
		if (!this.componentRef) {
			return;
		}

		this.appRef.detachView(this.componentRef.hostView);
		this.componentRef.destroy();
		this.componentRef = null;
		this.isDisplayed = false;
	}

	private createDiagnostic(error: HttpErrorResponse, requestMethod: string, requestUrl: string): MaintenanceDiagnostic {
		const status = Number(error.status ?? 0);
		const isOnline = typeof navigator === 'undefined' ? null : navigator.onLine;
		const errorCode = status === 0
			? `NET-0-NAV-${isOnline === false ? 'OFF' : 'ON'}`
			: `HTTP-${status}`;

		let server = 'unbekannt';
		let operation = 'Serveranfrage';
		try {
			const parsedUrl = new URL(requestUrl, document.baseURI);
			server = parsedUrl.hostname || server;
			operation = SAFE_OPERATION_NAMES[parsedUrl.pathname] ?? operation;
		} catch {
			// Keep the safe fallback. The request URL is deliberately never exposed.
		}

		const occurredAt = new Date();
		const apiErrorCode = this.safeDiagnosticToken(error.error?.error_code);
		const requestId = this.safeDiagnosticToken(error.headers?.get('X-Request-ID') ?? error.error?.request_id);
		const retryAfter = this.safeRetryAfter(error.headers?.get('Retry-After'));
		return {
			status,
			errorCode,
			technicalStatus: this.technicalStatus(status),
			diagnosis: this.diagnosis(status, isOnline),
			apiErrorCode,
			requestId,
			clientReference: this.createClientReference(),
			occurredAt: occurredAt.toISOString(),
			displayTime: occurredAt.toLocaleString('de-DE'),
			connection: isOnline === null ? 'unbekannt' : isOnline ? 'online' : 'offline',
			networkType: this.networkType(),
			frontend: typeof window === 'undefined' ? 'unbekannt' : window.location.hostname,
			build: document.querySelector<HTMLMetaElement>('meta[name="build-date"]')?.content || 'unbekannt',
			server,
			request: `${requestMethod.toUpperCase()} – ${operation}`,
			browser: this.describeBrowserAndSystem(typeof navigator === 'undefined' ? '' : navigator.userAgent),
			retryAfter
		};
	}

	private technicalStatus(status: number): string {
		const names: Record<number, string> = {
			0: 'HTTP 0 / Unknown Error',
			408: 'HTTP 408 / Request Timeout',
			429: 'HTTP 429 / Too Many Requests',
			500: 'HTTP 500 / Internal Server Error',
			502: 'HTTP 502 / Bad Gateway',
			503: 'HTTP 503 / Service Unavailable',
			504: 'HTTP 504 / Gateway Timeout'
		};
		return names[status] ?? `HTTP ${status}`;
	}

	private diagnosis(status: number, isOnline: boolean | null): string {
		if (status === 0 && isOnline === false) {
			return 'Keine Serverantwort. Der Browserstatus navigator.onLine war in diesem Moment „offline“. Das ist nur ein Hinweis und kein Beleg dafür, dass das Gerät keine Internetverbindung hatte.';
		}
		if (status === 0) {
			return 'Keine auswertbare Serverantwort. Möglich sind Netzwerkfilter, DNS-, TLS- oder CORS-Probleme, VPN oder Browser-Erweiterungen.';
		}
		if (status === 408) {
			return 'Der Server hat die Anfrage nicht rechtzeitig vollständig erhalten.';
		}
		if (status === 429) {
			return 'Das Anfrage- oder PIN-Versuchslimit wurde erreicht.';
		}
		if (status === 500) {
			return 'Die API hat einen internen Fehler gemeldet. Die Request-ID kann im Serverlog gesucht werden.';
		}
		if (status === 502) {
			return 'Ein Gateway hat keine gültige Antwort vom API-Server erhalten.';
		}
		if (status === 503) {
			return 'Der API-Server ist vorübergehend nicht verfügbar oder im Wartungsmodus.';
		}
		return 'Ein Gateway hat nicht rechtzeitig eine Antwort vom API-Server erhalten.';
	}

	private safeDiagnosticToken(value: unknown): string {
		const token = typeof value === 'string' ? value.trim() : '';
		return /^[A-Za-z0-9_-]{1,80}$/.test(token) ? token : 'nicht verfügbar';
	}

	private safeRetryAfter(value: unknown): string {
		const seconds = typeof value === 'string' ? value.trim() : '';
		return /^\d{1,7}$/.test(seconds) ? `${seconds} Sekunden` : 'nicht angegeben';
	}

	private createClientReference(): string {
		if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
			const values = new Uint32Array(1);
			crypto.getRandomValues(values);
			return `CLIENT-${values[0].toString(16).padStart(8, '0').toUpperCase()}`;
		}
		return `CLIENT-${Date.now().toString(16).slice(-8).toUpperCase()}`;
	}

	private networkType(): string {
		if (typeof navigator === 'undefined') {
			return 'unbekannt';
		}
		const connection = (navigator as Navigator & { connection?: { effectiveType?: string; saveData?: boolean } }).connection;
		if (!connection?.effectiveType) {
			return 'nicht vom Browser bereitgestellt';
		}
		return connection.saveData ? `${connection.effectiveType} (Datensparmodus)` : connection.effectiveType;
	}

	private describeBrowserAndSystem(userAgent: string): string {
		let browser = 'Unbekannter Browser';
		if (/SamsungBrowser/i.test(userAgent)) {
			browser = 'Samsung Internet';
		} else if (/Edg(?:A|iOS)?\//i.test(userAgent)) {
			browser = 'Edge';
		} else if (/(?:OPR|Opera)[/ ]/i.test(userAgent)) {
			browser = 'Opera';
		} else if (/(?:CriOS|Chrome)\//i.test(userAgent)) {
			browser = 'Chrome';
		} else if (/(?:FxiOS|Firefox)\//i.test(userAgent)) {
			browser = 'Firefox';
		} else if (/Safari\//i.test(userAgent)) {
			browser = 'Safari';
		}

		let system = 'Unbekanntes System';
		if (/Android/i.test(userAgent)) {
			system = 'Android';
		} else if (/(?:iPhone|iPad|iPod)/i.test(userAgent)) {
			system = 'iOS/iPadOS';
		} else if (/Windows/i.test(userAgent)) {
			system = 'Windows';
		} else if (/(?:Macintosh|Mac OS X)/i.test(userAgent)) {
			system = 'macOS';
		} else if (/Linux/i.test(userAgent)) {
			system = 'Linux';
		}

		return `${browser} / ${system}`;
	}
}
